/* eslint-disable max-len */
/* eslint-disable react/jsx-no-bind, consistent-return */
/* eslint-disable array-element-newline */
import React, {useCallback, useEffect, useRef, useState} from "react";
import AddressInfoContainer from "./AddressInfoContainer";
import BillingInfoContainer from "./BillingInfoContainer";
import CartHelper from "../../shared/modules/CartHelper";
import CustomModal from "../../shared/components/CustomModal";
import NonMemberCart from "./NonMemberCart";
import NonMemberCartAPI from "../../shared/modules/NonMemberCartAPI";
import NonMemberOrderConfirmation from "./NonMemberOrderConfirmation";
import Notification from "../../shared/components/Notification";
import PropTypes from "prop-types";
import StripeCheckoutPage from "./StripeCheckoutPage";
import {withCookies} from "react-cookie";

const NonMemberCartModal = ({
  cookies,
  enable2024Legal,
  handleCloseCartModal,
  handleDisplayPurchaseNotification,
  isCartModalOpen,
  recaptchaSiteKey,
  recurlyPublicKey,
  sessionId,
  setSessionId,
  stripeEnabled,
  stripePromise
}) => {
  const modalRef = useRef(null);

  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [cartInfo, setCartInfo] = useState(null);
  const [cartTotals, setCartTotals] = useState(null);
  const [billingInfo, setBillingInfo] = useState({});
  const [addressInfo, setAddressInfo] = useState(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const [promoCode, setPromoCode] = useState(cookies.get("auto_applied_promo_code"));
  const [promos, setPromos] = useState([]);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [isOrderPlaced, setIsOrderPlaced] = useState(null);

  const [error, setError] = useState(null);
  const [displayErrors, setDisplayErrors] = useState(false);

  const [notificationText, setNotificationText] = useState(null);

  useEffect(() => {
    const handleClick = () => {
      setDisplayErrors(false);
      setError(null);
    };

    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  const refreshCart = () => {
    NonMemberCartAPI.getCartInfo(promoCode).then((response) => {
      setCartInfo(response.order_detail);
      setCartTotals(response.totals);
      setPromos(response.cart_promos);
    });
  };

  useEffect(() => {
    NonMemberCartAPI.getCartInfo(promoCode).then((response) => {
      setCartInfo(response.order_detail);
      setCartTotals(response.totals);
      setAddressInfo(response.contact_info || {});
      setPromos(response.cart_promos);
      setLoadingPage(false);
      setSmsOptIn(response.sms_opted_in);
    });
  }, [isEditingAddress, isEditingBilling]);

  useEffect(() => {
    if (notificationText) {
      const timeout = setTimeout(() => {
        setNotificationText(null);
      }, 6000);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [notificationText]);

  const fetchClientSecret = useCallback(() => {
    const contactId = addressInfo.id;

    return NonMemberCartAPI.purchaseCart(null, contactId, captchaToken, promoCode).then((response) => {
      if (response.error) {
        CartHelper.handlePurchaseError(response, setNotificationText);
        setShowStripeCheckout(false);
      }
      setSessionId(response.session_id);
      return response.client_secret;
    });

  }, [addressInfo, captchaToken, promoCode]);

  const options = {fetchClientSecret};

  return (
    <CustomModal
      handleCloseModal={handleCloseCartModal}
      modalClassName="cart-modal"
      ref={modalRef}
      show={isCartModalOpen}
    >
      {
        displayErrors && error &&
        <div className="cart-modal-error non-member-error">
          {error.message ? error.message : "There was an error updating your information."}
        </div>
      }
      {!isEditingAddress && !isEditingBilling && !showStripeCheckout && !isOrderPlaced &&
        <NonMemberCart
          addressInfo={addressInfo}
          billingInfo={billingInfo}
          captchaToken={captchaToken}
          cartInfo={cartInfo}
          cartTotals={cartTotals}
          handleCloseCartModal={handleCloseCartModal}
          handleDisplayPurchaseNotification={handleDisplayPurchaseNotification}
          loadingPage={loadingPage}
          promoCode={promoCode}
          promos={promos}
          recaptchaSiteKey={recaptchaSiteKey}
          refreshCart={refreshCart}
          setCaptchaToken={setCaptchaToken}
          setCartInfo={setCartInfo}
          setCartTotals={setCartTotals}
          setIsEditingAddress={setIsEditingAddress}
          setIsEditingBilling={setIsEditingBilling}
          setNotificationText={setNotificationText}
          setPromoCode={setPromoCode}
          setPromos={setPromos}
          setShowStripeCheckout={setShowStripeCheckout}
          stripeEnabled={stripeEnabled}
        />
      }
      {isEditingAddress &&
        <AddressInfoContainer
          addressInfo={addressInfo}
          enable2024Legal={enable2024Legal}
          modalRef={modalRef}
          setAddressInfo={setAddressInfo}
          setBillingInfo={setBillingInfo}
          setDisplayErrors={setDisplayErrors}
          setError={setError}
          setIsEditingAddress={setIsEditingAddress}
          setSmsOptIn={setSmsOptIn}
          smsOptIn={smsOptIn}
        />
      }
      {isEditingBilling &&
        <BillingInfoContainer
          billingInfo={billingInfo}
          recurlyPublicKey={recurlyPublicKey}
          setBillingInfo={setBillingInfo}
          setDisplayErrors={setDisplayErrors}
          setError={setError}
          setIsEditingBilling={setIsEditingBilling}
        />
      }
      {
        showStripeCheckout &&
        <StripeCheckoutPage
          options={options}
          sessionId={sessionId}
          setIsOrderPlaced={setIsOrderPlaced}
          setNotificationText={setNotificationText}
          setSessionId={setSessionId}
          setShowStripeCheckout={setShowStripeCheckout}
          stripePromise={stripePromise}
        />
      }
      {
        isOrderPlaced && sessionId &&
        <NonMemberOrderConfirmation
          addressInfo={addressInfo}
          sessionId={sessionId}
          setDisplayErrors={setDisplayErrors}
          setError={setError}
        />
      }
      {notificationText && <Notification notificationDisplayText={notificationText} />}
    </CustomModal>
  );
};

NonMemberCartModal.propTypes = {
  "cookies": PropTypes.object.isRequired,
  "enable2024Legal": PropTypes.bool.isRequired,
  "handleCloseCartModal": PropTypes.func.isRequired,
  "handleDisplayPurchaseNotification": PropTypes.func.isRequired,
  "isCartModalOpen": PropTypes.bool.isRequired,
  "recaptchaSiteKey": PropTypes.string.isRequired,
  "recurlyPublicKey": PropTypes.string.isRequired,
  "sessionId": PropTypes.string,
  "setSessionId": PropTypes.func.isRequired,
  "stripeEnabled": PropTypes.bool.isRequired,
  "stripePromise": PropTypes.object
};

NonMemberCartModal.defaultProps = {
  "sessionId": null,
  "stripePromise": {}
};
export default withCookies(NonMemberCartModal);
